// src/app/components/google/google-connector-status.tsx
//
// Renders inline inside IntegrationDetailPanel for the two read-only Google
// connector cards, the same way PublishingIntegrationStatus does for publishing
// destinations: live connection status, and for admins Connect / Sync now /
// Disconnect.
//
// Non-admins see status only: no Connect, no Sync, no Disconnect, no paste field.
// They may read which property a number came from and when it was last read,
// because that is the provenance of a figure already on their screen.
//
// No key is ever displayed here, because no endpoint returns one. The only
// credential detail on screen is the service account address, which is not
// secret and which an operator has to see to confirm the grant landed on the
// right account.
//
// Values come from the token layer rather than from the quarantined pre-token
// variables, and the controls are kit components. This is a new file, so it is
// held to the full rule from its first line.
import React, { useEffect, useState } from "react";
import {
  AlertTriangle, CheckCircle2, ChevronDown, ChevronUp, Plug, RefreshCw, Unplug,
} from "lucide-react";
import { toast } from "sonner";
import {
  classifyGoogleError, googleBoundTo, googleConnectorsApi, googleProviderLabel,
  type GoogleProvider, type GoogleProviderStatus,
} from "@/app/services/analytics/google-connectors-api";
import { exactTime, relativeTime } from "@/app/services/analytics/relative-time";
import { ConfirmDialog } from "@/app/components/shared/confirm-dialog";
import { ConnectGoogleModal } from "./connect-google-modal";
import { Button, Icon, Spinner } from "../kit";

interface GoogleConnectorStatusProps {
  provider: GoogleProvider;
  brandName?: string;
}

/** Same storage-backed check the rest of the app uses (top-nav-bar, upmode-home).
 *  The API enforces admin on every write path regardless; this only decides what
 *  is worth rendering. */
function viewerIsAdmin(): boolean {
  return (
    localStorage.getItem("is_admin") === "true" ||
    sessionStorage.getItem("is_admin") === "true"
  );
}

export function GoogleConnectorStatus({ provider, brandName }: GoogleConnectorStatusProps) {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<GoogleProviderStatus | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const isAdmin = viewerIsAdmin();

  const load = async () => {
    if (!brandName) {
      setStatus(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await googleConnectorsApi.status(brandName);
      setStatus(res.providers?.[provider] ?? null);
    } catch {
      // The page itself surfaces a global failure. Here an unreadable status is
      // left as "not connected" rather than shouting.
      setStatus(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider, brandName]);

  const handleSync = async () => {
    if (!brandName) return;
    setSyncing(true);
    try {
      const res = await googleConnectorsApi.sync(provider, brandName);
      const rows = res.result?.rows ?? 0;
      if (res.result?.status === "ok") {
        // A read that succeeds and returns nothing is a success. Saying "0 days
        // read" reads as a failure and sends an operator looking for a fault
        // that is not there: Google simply has no day for this property yet.
        toast.success(
          rows === 0
            ? "Read complete. Google has no days for this property yet."
            : `Read complete: ${rows} days.`,
        );
      } else {
        toast.error(res.result?.error || "The sync did not complete.");
      }
      setStatus(res);
    } catch (e: any) {
      toast.error(e?.message || "The sync did not complete.");
    } finally {
      setSyncing(false);
    }
  };

  const handleDisconnect = async () => {
    if (!brandName) return;
    setDisconnecting(true);
    try {
      await googleConnectorsApi.disconnect(provider, brandName);
      toast.success(`${googleProviderLabel(provider)} disconnected`);
      await load();
    } catch (e: any) {
      toast.error(e?.message || "Disconnect failed");
    } finally {
      setDisconnecting(false);
      setDisconnectOpen(false);
    }
  };

  const connected = !!status?.connected;
  const hasError = connected && status?.status !== "active";

  return (
    <>
      <div
        className="p-k-5 rounded-k-lg"
        style={{
          backgroundColor: "var(--s-sunken)",
          border: "var(--bd-w) solid var(--bd-default)",
        }}
      >
        <div className="flex items-start justify-between gap-k-3 mb-k-4 flex-wrap">
          <h3
            className="text-k-sub text-tx-strong"
            style={{ fontWeight: "var(--fw-semibold)" }}
          >
            Connection status
          </h3>
          {!loading && <StatusBadge connected={connected} hasError={hasError} />}
        </div>

        {loading && (
          <div className="flex items-center gap-k-2 text-tx-muted">
            <Spinner />
            <span className="text-k-small">Checking status…</span>
          </div>
        )}

        {!loading && !brandName && (
          <p className="text-k-body text-tx-default">
            Select a brand to see whether this connector is connected.
          </p>
        )}

        {!loading && brandName && !connected && (
          <>
            <p className="text-k-body text-tx-default mb-k-4">
              Not connected. The Outcomes view shows sample data for this series
              until it is.
            </p>
            {isAdmin ? (
              <Button onClick={() => setModalOpen(true)} icon={Plug}>
                Connect {googleProviderLabel(provider)}
              </Button>
            ) : (
              <p className="text-k-small text-tx-muted">
                An Upmode administrator connects this.
              </p>
            )}
          </>
        )}

        {!loading && brandName && connected && status && (
          <ConnectedView
            status={status}
            isAdmin={isAdmin}
            syncing={syncing}
            onSync={handleSync}
            onReconnect={() => setModalOpen(true)}
            onDisconnect={() => setDisconnectOpen(true)}
          />
        )}
      </div>

      {modalOpen && brandName && (
        <ConnectGoogleModal
          provider={provider}
          brandName={brandName}
          onClose={() => setModalOpen(false)}
          onConnected={() => {
            setModalOpen(false);
            load();
          }}
        />
      )}

      <ConfirmDialog
        open={disconnectOpen}
        onOpenChange={(open) => { if (!open) setDisconnectOpen(false); }}
        onConfirm={handleDisconnect}
        title={`Disconnect ${googleProviderLabel(provider)}?`}
        description="The key and the data read from this property are deleted. Outcomes goes back to sample data for this series. You can reconnect later."
        confirmLabel={disconnecting ? "Disconnecting…" : "Disconnect"}
        destructive
      />
    </>
  );
}

// ─── Status badge ────────────────────────────────────────────────────────────

function StatusBadge({ connected, hasError }: { connected: boolean; hasError: boolean }) {
  if (!connected) {
    return (
      <span style={chipSt("var(--s-subtle)", "var(--tx-muted)", "var(--bd-default)")}>
        Not connected
      </span>
    );
  }
  if (hasError) {
    return (
      <span style={chipSt("var(--st-err-bg)", "var(--st-err-t)", "var(--st-err-bd)")}>
        <Icon icon={AlertTriangle} size="sm" />
        Needs attention
      </span>
    );
  }
  return (
    <span style={chipSt("var(--st-ok-bg)", "var(--st-ok-t)", "var(--st-ok-bd)")}>
      <Icon icon={CheckCircle2} size="sm" />
      Connected
    </span>
  );
}

function chipSt(bg: string, color: string, border: string): React.CSSProperties {
  return {
    display: "inline-flex",
    alignItems: "center",
    gap: "var(--sp-1)",
    padding: "var(--sp-1) var(--sp-3)",
    borderRadius: "var(--r-full)",
    backgroundColor: bg,
    color,
    border: `var(--bd-w) solid ${border}`,
    fontSize: "var(--t-micro)",
    fontWeight: "var(--fw-semibold)",
    textTransform: "uppercase",
    letterSpacing: "var(--t-micro-tr)",
    whiteSpace: "nowrap",
  };
}

// ─── The error block ─────────────────────────────────────────────────────────
//
// Three cases, one plain sentence each, and the raw text Google returned behind
// a disclosure. The classification is the API module's, so the same failure
// reads the same way anywhere it is shown; this component only renders it.
//
// The raw error is kept rather than dropped. It is the only thing that helps
// when the classification is wrong, and an operator who has to ask an engineer
// what happened is worse off than one who can paste the sentence.
//
// There is no disclosure in the kit, so this is a kit ghost button toggling a
// muted paragraph, which is what the two would compose to.

function ErrorBlock({ lastError }: { lastError: string | null }) {
  const [showDetails, setShowDetails] = useState(false);
  const copy = classifyGoogleError(lastError);

  return (
    <div
      className="p-k-4 mb-k-4 rounded-k-md"
      style={{
        backgroundColor: "var(--st-err-bg)",
        border: "var(--bd-w) solid var(--st-err-bd)",
      }}
    >
      <div className="flex items-start gap-k-2">
        <Icon icon={AlertTriangle} size="sm" className="shrink-0 text-st-err-t" />
        <div className="min-w-0">
          <p
            className="text-k-small text-st-err-t mb-k-1"
            style={{ fontWeight: "var(--fw-semibold)" }}
          >
            {copy.title}
          </p>
          <p className="text-k-small text-tx-default">{copy.body}</p>
          {lastError && (
            <div className="mt-k-2">
              <Button
                variant="ghost"
                onClick={() => setShowDetails((open) => !open)}
                icon={showDetails ? ChevronUp : ChevronDown}
              >
                Details
              </Button>
              {showDetails && (
                <p className="text-k-small text-tx-muted mt-k-2 break-words">
                  {lastError}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Connected view ──────────────────────────────────────────────────────────

function ConnectedView({
  status, isAdmin, syncing, onSync, onReconnect, onDisconnect,
}: {
  status: GoogleProviderStatus;
  isAdmin: boolean;
  syncing: boolean;
  onSync: () => void;
  onReconnect: () => void;
  onDisconnect: () => void;
}) {
  const boundTo = googleBoundTo(status);
  const hasError = status.status !== "active";

  const rows: Array<{ label: string; value: React.ReactNode }> = [];

  if (status.service_account_email) {
    rows.push({ label: "Service account", value: status.service_account_email });
  }
  if (boundTo) {
    rows.push({
      label: status.provider === "ga4" ? "Property" : "Search Console property",
      value: boundTo,
    });
  }
  if (status.provider === "gsc" && status.brand_terms.length > 0) {
    rows.push({ label: "Brand terms", value: status.brand_terms.join(", ") });
  }
  // Never a zero for an absence: a connector that has not read says so in words,
  // and a connector holding no days says that rather than showing 0.
  //
  // Elapsed time on screen, the exact instant in the title. "Is this fresh?" is
  // the question a timestamp is asked, and a date answers it only after the
  // reader does the subtraction.
  const lastRead = relativeTime(status.last_sync_at);
  const lastReadExact = exactTime(status.last_sync_at);
  rows.push({
    label: "Last read",
    value: lastRead
      ? <span title={lastReadExact ?? undefined}>{lastRead}</span>
      : "No read yet",
  });
  rows.push({
    label: "Days of data",
    value: status.daily_row_count > 0
      ? String(status.daily_row_count)
      : "No data yet",
  });

  return (
    <>
      {hasError && <ErrorBlock lastError={status.last_error} />}

      <dl className="mb-k-5">
        {rows.map((r, i) => (
          <div key={i} className="flex items-start gap-k-3 mb-k-2">
            <dt
              className="text-k-small text-tx-muted"
              style={{ minWidth: "var(--sp-20)" }}
            >
              {r.label}
            </dt>
            <dd className="text-k-small text-tx-default flex-1 break-words">
              {r.value}
            </dd>
          </div>
        ))}
      </dl>

      {isAdmin && (
        <div className="flex flex-wrap gap-k-2">
          <Button variant="secondary" onClick={onSync} loading={syncing} icon={RefreshCw}>
            {syncing ? "Reading…" : "Read now"}
          </Button>
          <Button variant="secondary" onClick={onReconnect} icon={Plug}>
            {hasError ? "Reconnect" : "Update key or property"}
          </Button>
          <Button variant="secondary" onClick={onDisconnect} icon={Unplug}>
            Disconnect
          </Button>
        </div>
      )}
    </>
  );
}
